import { useMemo } from 'react';

import RovoIcon from '@atlaskit/icon-lab/core/rovo';
import BookOpenIcon from '@atlaskit/icon-lab/core/book-open';
import FlaskIcon from '@atlaskit/icon/core/flask';
import AssetsObjectTypeIcon from '@atlaskit/icon-lab/core/assets-object-type';
import { useAtlassianUserContext } from '../../hooks/use-atlassian-user';
import AssetsGraphIcon from '@atlaskit/icon-lab/core/assets-graph';
import { fg } from '@atlaskit/platform-feature-flags';
import type { NewCoreIconProps } from '@atlaskit/icon';

export type VisibleApp = {
	name: string;
	link: string;
	icon: React.ComponentType<NewCoreIconProps>;
};

const catalogApp: VisibleApp = {
	name: 'Catalog',
	link: '/catalog',
	icon: BookOpenIcon,
};

const allApps: VisibleApp[] = [
	{
		name: 'Assistant',
		link: '/assistant',
		icon: RovoIcon,
	},
	{
		name: 'Labs',
		link: '/labs',
		icon: FlaskIcon,
	},
	{
		name: 'Visualiser',
		link: '/visualiser',
		icon: AssetsObjectTypeIcon,
	},
];

const graphExplorerApp: VisibleApp = {
	name: 'Graph Explorer',
	link: '/graph-explorer',
	icon: AssetsGraphIcon,
};

export type VisibleAppsResult = {
	apps: VisibleApp[];
	loading: boolean;
};

/**
 * Get the list of apps visible to the current user based on:
 * - Atlassian employees: see all apps
 * - Non-employees: see no apps
 */
export function useVisibleApps(): VisibleAppsResult {
	const { identity, loading } = useAtlassianUserContext();

	const apps = useMemo(() => {
		if (loading) {
			return [];
		}

		if (identity.isAtlassianEmployee) {
			const appsList = fg('twg-graphyte-gc-show-catalog') ? [catalogApp, ...allApps] : [...allApps];
			if (fg('graph_explorer_in_super_app')) {
				appsList.push(graphExplorerApp);
			}
			return appsList;
		}

		return [];
	}, [identity.isAtlassianEmployee, loading]);

	return { apps, loading };
}
